import type {
  WordChantRenderScene,
  WordChantRenderSpec,
  WordChantWordRenderScene,
} from './schema';
import {
  WORD_CHANT_MIN_WORD_COUNT,
  WORD_CHANT_MAX_WORD_COUNT,
  resolveWordChantDefaultCueDisplayText,
} from '../lib/word-chant-contract';

export interface WordChantThumbnailPosition {
  x: number;
  y: number;
  scale: number;
}

export interface WordChantThumbnailDeckMetrics {
  bottom: number;
  cardWidth: number;
  cardHeight: number;
  positionScale: number;
}

export interface WordChantThumbnailEntranceTiming {
  startFrame: number;
  durationInFrames: number;
  settledFrame: number;
  isImmediate: boolean;
}

export interface WordChantCardFlipFrames {
  startFrame: number;
  centerFrame: number;
  endFrame: number;
  isInstant: boolean;
}

export const WORD_CHANT_BGM_RAMP_FRAMES = 5;
export const WORD_CHANT_BGM_DUCKING_GAIN = 10 ** (-2 / 20);
export const WORD_CHANT_CURRENT_BGM_DUCKING_GAIN = 10 ** (-1 / 20);
export const WORD_CHANT_INTRO_TITLE_ENTRANCE_FRAME = 32;
export const WORD_CHANT_INTRO_READY_FRAME = 98;
export const WORD_CHANT_INTRO_SET_FRAME = 117;
export const WORD_CHANT_INTRO_GO_FRAME = 144;
export const RECORDED_SCENE_TRANSITION_MAX_FRAMES = 30;
export const RECORDED_VISUAL_LEAD_FRAMES = 7;
export const RECORDED_WORD_SPEECH_LEAD_FRAMES = 4;
export const RECORDED_GREAT_MIN_DISPLAY_FRAMES = 36;
export const RECORDED_GAUGE_FIRST_FILL_FRAMES = 32;
export const RECORDED_GAUGE_FINAL_FILL_FRAMES = 24;

export function introTitleEntranceFrameForVersion(
  version: WordChantRenderSpec['version'],
): number {
  return version === 'word_chant.render_spec.v7'
    ? WORD_CHANT_INTRO_TITLE_ENTRANCE_FRAME
    : 0;
}

export function introCueTextForFrame(
  version: WordChantRenderSpec['version'],
  frame: number,
): 'Ready' | 'Set' | 'Go!' | null {
  if (version !== 'word_chant.render_spec.v7') {
    return frame < 62 ? 'Ready' : frame < 116 ? 'Set' : 'Go!';
  }
  if (frame < WORD_CHANT_INTRO_READY_FRAME) return null;
  if (frame < WORD_CHANT_INTRO_SET_FRAME) return 'Ready';
  if (frame < WORD_CHANT_INTRO_GO_FRAME) return 'Set';
  return 'Go!';
}

export function progressForFrame(
  frame: number,
  durationFrames: number,
): number {
  if (durationFrames <= 1) return 1;
  return Math.min(1, Math.max(0, frame / (durationFrames - 1)));
}

export function shouldShowProgressRail(
  version: WordChantRenderSpec['version'],
): boolean {
  return version !== 'word_chant.render_spec.v8';
}

export const RECORDED_SOUND_WAVE_CYCLE_FRAMES = 18;

export function recordedSoundWaveCount(localFrame: number): number {
  const cycleFrame =
    ((Math.floor(localFrame) % RECORDED_SOUND_WAVE_CYCLE_FRAMES) +
      RECORDED_SOUND_WAVE_CYCLE_FRAMES) %
    RECORDED_SOUND_WAVE_CYCLE_FRAMES;
  if (cycleFrame < 3 || cycleFrame >= 16) return 0;
  if (cycleFrame < 7) return 1;
  if (cycleFrame < 11) return 2;
  return 3;
}

export function recordedProgressGaugeProgress(
  frame: number,
  scenes: readonly Pick<
    WordChantRenderScene,
    'kind' | 'startFrame' | 'endFrame'
  >[],
  cues: readonly { text: string; startFrame: number }[],
): number | null {
  const interlude = scenes.find((scene) => scene.kind === 'interlude');
  const outro = scenes.find((scene) => scene.kind === 'outro');
  if (!interlude || !outro || outro.startFrame <= interlude.startFrame) {
    return null;
  }

  const firstInterludeCue = cues
    .filter(
      (cue) =>
        interlude.startFrame <= cue.startFrame &&
        cue.startFrame < interlude.endFrame,
    )
    .sort((left, right) => left.startFrame - right.startFrame)[0];
  const firstOutroCue = cues
    .filter(
      (cue) =>
        outro.startFrame <= cue.startFrame && cue.startFrame < outro.endFrame,
    )
    .sort((left, right) => left.startFrame - right.startFrame)[0];

  const firstFillStart = recordedVisualStartFrame(
    firstInterludeCue?.startFrame ?? interlude.startFrame,
  );
  const finalFillStart = recordedVisualStartFrame(
    firstOutroCue?.startFrame ?? outro.startFrame,
  );
  if (finalFillStart <= firstFillStart || frame < firstFillStart) return null;

  if (frame < finalFillStart) {
    return (
      Math.min(
        1,
        Math.max(
          0,
          (frame - firstFillStart) / RECORDED_GAUGE_FIRST_FILL_FRAMES,
        ),
      ) * 0.5
    );
  }

  return (
    0.5 +
    Math.min(
      1,
      Math.max(0, (frame - finalFillStart) / RECORDED_GAUGE_FINAL_FILL_FRAMES),
    ) *
      0.5
  );
}

export function isRecordedProgressGaugeVisible(
  frame: number,
  scenes: readonly Pick<
    WordChantRenderScene,
    'kind' | 'startFrame' | 'endFrame'
  >[],
): boolean {
  return scenes.some((scene) => {
    if (scene.kind !== 'interlude' && scene.kind !== 'outro') return false;
    const startFrame =
      recordedVisualStartFrame(scene.startFrame) +
      recordedSceneTransitionDuration(scene.endFrame - scene.startFrame);
    const endFrame =
      scene.kind === 'outro'
        ? scene.endFrame
        : recordedVisualStartFrame(scene.endFrame);
    return startFrame <= frame && frame < endFrame;
  });
}

export function thumbnailAssetIdForVersion(
  version: WordChantRenderSpec['version'],
  word: Pick<
    WordChantRenderSpec['words'][number],
    'illustrationAssetId' | 'photoAssetId'
  >,
): string {
  return version === 'word_chant.render_spec.v8'
    ? word.illustrationAssetId
    : word.photoAssetId;
}

export function sceneAtFrame(
  spec: WordChantRenderSpec,
  frame: number,
): WordChantRenderScene {
  const boundedFrame = Math.min(
    Math.max(0, Math.floor(frame)),
    spec.durationFrames - 1,
  );
  const scene =
    spec.scenes.find(
      (scene) =>
        scene.startFrame <= boundedFrame && boundedFrame < scene.endFrame,
    ) ?? spec.scenes.at(-1);
  if (!scene) {
    throw new RangeError('Word Chant render spec must contain scenes');
  }
  return scene as WordChantRenderScene;
}

export function backgroundMusicVolumeForFrame(
  spec: WordChantRenderSpec,
  frame: number,
  baseVolume: number,
): number {
  if (
    spec.version !== 'word_chant.render_spec.v4' &&
    spec.version !== 'word_chant.render_spec.v5' &&
    spec.version !== 'word_chant.render_spec.v6' &&
    spec.version !== 'word_chant.render_spec.v7'
  ) {
    return baseVolume;
  }
  const boundedFrame = Math.min(
    Math.max(0, Math.floor(frame)),
    spec.durationFrames - 1,
  );
  const scene = sceneAtFrame(spec, boundedFrame);
  const duckingGain =
    spec.version === 'word_chant.render_spec.v5'
      ? WORD_CHANT_CURRENT_BGM_DUCKING_GAIN
      : WORD_CHANT_BGM_DUCKING_GAIN;
  const duckedVolume = baseVolume * duckingGain;
  if (scene.kind === 'word') {
    return duckedVolume;
  }

  const sceneIndex = spec.scenes.findIndex(
    (candidate) => candidate.sceneId === scene.sceneId,
  );
  const previousScene = spec.scenes[sceneIndex - 1];
  if (previousScene?.kind === 'word') {
    const rampProgress =
      (boundedFrame - previousScene.endFrame) / WORD_CHANT_BGM_RAMP_FRAMES;
    if (rampProgress <= 1) {
      return interpolateVolume(duckedVolume, baseVolume, rampProgress);
    }
  }

  const nextScene = spec.scenes[sceneIndex + 1];
  if (nextScene?.kind === 'word') {
    const rampProgress =
      (nextScene.startFrame - boundedFrame) / WORD_CHANT_BGM_RAMP_FRAMES;
    if (rampProgress <= 1) {
      return interpolateVolume(duckedVolume, baseVolume, rampProgress);
    }
  }

  return baseVolume;
}

export function shouldShowThumbnailLabels(spec: WordChantRenderSpec): boolean {
  return (
    spec.version !== 'word_chant.render_spec.v4' &&
    spec.version !== 'word_chant.render_spec.v5' &&
    spec.version !== 'word_chant.render_spec.v6' &&
    spec.version !== 'word_chant.render_spec.v7'
  );
}

export function shouldShowThumbnailDeck(spec: WordChantRenderSpec): boolean {
  return (
    spec.version !== 'word_chant.render_spec.v4' &&
    spec.version !== 'word_chant.render_spec.v5' &&
    spec.version !== 'word_chant.render_spec.v6' &&
    spec.version !== 'word_chant.render_spec.v7'
  );
}

function interpolateVolume(
  startVolume: number,
  endVolume: number,
  progress: number,
): number {
  const boundedProgress = Math.min(1, Math.max(0, progress));
  return startVolume + (endVolume - startVolume) * boundedProgress;
}

export function getThumbnailLayout(
  count: number,
  activeIndex: number,
): WordChantThumbnailPosition[] {
  if (
    !Number.isInteger(count) ||
    count < WORD_CHANT_MIN_WORD_COUNT ||
    count > WORD_CHANT_MAX_WORD_COUNT
  ) {
    throw new RangeError(
      `Word Chant thumbnail count must be between ${WORD_CHANT_MIN_WORD_COUNT} and ${WORD_CHANT_MAX_WORD_COUNT}`,
    );
  }
  const hasActiveWord = activeIndex >= 0 && activeIndex < count;
  const boundedActiveIndex = Math.min(count - 1, Math.max(0, activeIndex));
  if (count >= 6) {
    // 6개는 3+3, 7·8개는 4+3 / 4+4. 마지막 줄도 독립적으로 중앙 정렬한다.
    const columns = count === 6 ? 3 : 4;
    return Array.from({ length: count }, (_, index) => {
      const row = Math.floor(index / columns);
      const rowCount = Math.min(columns, count - row * columns);
      return {
        x: ((index % columns) - (rowCount - 1) / 2) * 230,
        y: (row - 0.5) * 150,
        scale: hasActiveWord && index === boundedActiveIndex ? 1.08 : 0.9,
      };
    });
  }

  const spacing = count === 5 ? 205 : 240;
  return Array.from({ length: count }, (_, index) => ({
    x: (index - (count - 1) / 2) * spacing,
    y: 0,
    scale:
      hasActiveWord && index === boundedActiveIndex
        ? 1.08
        : count === 5
          ? 0.88
          : 0.96,
  }));
}

export function getThumbnailDeckMetrics(
  count: number,
  mode: 'rail' | 'showcase',
  isRecorded = false,
): WordChantThumbnailDeckMetrics {
  if (
    !Number.isInteger(count) ||
    count < WORD_CHANT_MIN_WORD_COUNT ||
    count > WORD_CHANT_MAX_WORD_COUNT
  ) {
    throw new RangeError(
      `Word Chant thumbnail count must be between ${WORD_CHANT_MIN_WORD_COUNT} and ${WORD_CHANT_MAX_WORD_COUNT}`,
    );
  }
  if (mode === 'showcase') {
    if (!isRecorded) {
      return {
        bottom: 215,
        cardWidth: 210,
        cardHeight: 142,
        positionScale: 1.2,
      };
    }
    return {
      // 삽화가 중심인 세로형 카드 묶음. 6~8단어는 두 줄 사이 간격을 확보한다.
      bottom: count >= 6 ? 290 : 270,
      cardWidth: count >= 6 ? 220 : 280,
      cardHeight: count >= 6 ? 250 : 330,
      positionScale: count >= 6 ? 1.75 : 1.5,
    };
  }
  if (count >= 6) {
    return {
      bottom: 130,
      cardWidth: 110,
      cardHeight: 74,
      positionScale: 0.74,
    };
  }
  return {
    bottom: 64,
    cardWidth: 166,
    cardHeight: 112,
    positionScale: 0.74,
  };
}

export function getCardFlipFrames(
  scene: WordChantWordRenderScene,
): WordChantCardFlipFrames {
  const centerFrame = scene.flipFrame - scene.startFrame;
  const teacherEndFrame = scene.teacherEndFrame - scene.startFrame;
  const endFrame = scene.cueFrames[2] - scene.startFrame;
  if (teacherEndFrame >= centerFrame || centerFrame >= endFrame) {
    return {
      startFrame: endFrame,
      centerFrame: endFrame,
      endFrame,
      isInstant: true,
    };
  }
  return {
    startFrame: teacherEndFrame,
    centerFrame,
    endFrame,
    isInstant: false,
  };
}

export function wordSceneExitProgress(
  scene: WordChantWordRenderScene,
  localFrame: number,
): number {
  const photoEndFrame = scene.cueFrames[3] - scene.startFrame;
  const finalFrame = scene.durationFrames - 1;
  if (photoEndFrame >= finalFrame) return 0;
  const exitStartFrame = Math.max(photoEndFrame, finalFrame - 12);
  if (finalFrame <= exitStartFrame) return 0;
  return Math.min(
    1,
    Math.max(0, (localFrame - exitStartFrame) / (finalFrame - exitStartFrame)),
  );
}

export function wordSceneHandoffScaleX(progress: number): number {
  const boundedProgress = Math.min(1, Math.max(0, progress));
  if (boundedProgress === 0 || boundedProgress === 1) return 1;
  return 1 - Math.sin(boundedProgress * Math.PI) * 0.92;
}

export function recordedCueDurationInFrames(
  startFrame: number,
  endFrame: number,
): number {
  return Math.max(0, endFrame - startFrame);
}

export function recordedCueDisplayDurationInFrames(
  text: string,
  startFrame: number,
  endFrame: number,
  nextCueStartFrame?: number,
  compositionEndFrame?: number,
): number {
  const displayText = recordedCueDisplayText(text);
  if (compositionEndFrame !== undefined) {
    return Math.max(
      1,
      compositionEndFrame - recordedVisualStartFrame(startFrame),
    );
  }
  const canonicalDuration = recordedCueDurationInFrames(startFrame, endFrame);
  if (displayText !== 'Great!') return canonicalDuration;

  const readableDuration = Math.max(
    canonicalDuration,
    RECORDED_GREAT_MIN_DISPLAY_FRAMES,
  );
  if (nextCueStartFrame === undefined) return readableDuration;
  return Math.max(
    1,
    Math.min(readableDuration, nextCueStartFrame - startFrame),
  );
}

export function lastRecordedOutroCueIndex(
  cues: readonly { text: string; startFrame: number }[],
  outroStartFrame: number,
): number {
  let result = -1;
  let latestStartFrame = -1;
  cues.forEach((cue, index) => {
    if (cue.startFrame < outroStartFrame) return;
    if (cue.startFrame >= latestStartFrame) {
      result = index;
      latestStartFrame = cue.startFrame;
    }
  });
  return result;
}

export function recordedCueExitOpacity(
  canonicalText: string | null,
  localFrame: number,
  durationInFrames: number,
): number {
  if (
    !canonicalText ||
    !['Great!', 'Faster!'].includes(canonicalText) ||
    durationInFrames <= 1
  ) {
    return 1;
  }
  const finalFrame = durationInFrames - 1;
  const exitStartFrame = Math.max(0, durationInFrames - 8);
  if (localFrame <= exitStartFrame) return 1;
  return Math.max(
    0,
    1 - (localFrame - exitStartFrame) / (finalFrame - exitStartFrame),
  );
}

export function recordedVisualStartFrame(frame: number): number {
  return Math.max(0, frame - RECORDED_VISUAL_LEAD_FRAMES);
}

export function recordedDecorationFrame(
  sceneStartFrame: number,
  localFrame: number,
): number {
  return recordedVisualStartFrame(sceneStartFrame) + localFrame;
}

export function recordedAudioAlignedFrame(localVisualFrame: number): number {
  return localVisualFrame - RECORDED_VISUAL_LEAD_FRAMES;
}

export function recordedTransitionAudioFrame(
  sceneDurationFrames: number,
  transitionFrame: number,
): number {
  return Math.max(
    0,
    sceneDurationFrames - RECORDED_VISUAL_LEAD_FRAMES + transitionFrame,
  );
}

export function recordedWordSpeechScale(
  localAudioFrame: number,
  cueFrames: readonly [number, number, number, number],
): number {
  let scale = 1;
  for (const [startFrame, endFrame] of [
    [cueFrames[0], cueFrames[1]],
    [cueFrames[2], cueFrames[3]],
  ] as const) {
    const pronunciationDuration = Math.max(1, (endFrame - startFrame) / 2);
    const entranceDuration = Math.min(
      RECORDED_WORD_SPEECH_LEAD_FRAMES,
      Math.max(0.5, pronunciationDuration / 2),
    );
    for (const pronunciationIndex of [0, 1] as const) {
      const pronunciationStartFrame =
        startFrame + pronunciationDuration * pronunciationIndex;
      const entranceStartFrame = pronunciationStartFrame - entranceDuration;
      const exitFrame =
        pronunciationIndex === 0
          ? pronunciationStartFrame + pronunciationDuration
          : endFrame;
      if (
        localAudioFrame < entranceStartFrame ||
        localAudioFrame >= exitFrame
      ) {
        continue;
      }
      if (localAudioFrame < pronunciationStartFrame) {
        const entranceProgress =
          (localAudioFrame - entranceStartFrame) / entranceDuration;
        scale = Math.max(scale, 1 + entranceProgress * 0.1);
        continue;
      }
      const exitDuration = Math.max(1, exitFrame - pronunciationStartFrame);
      const exitProgress =
        (localAudioFrame - pronunciationStartFrame) / exitDuration;
      scale = Math.max(scale, 1 + (1 - exitProgress) * 0.1);
    }
  }
  return scale;
}

export function recordedThumbnailEntranceTiming(
  sceneDurationFrames: number,
  wordCount: number,
  wordIndex: number,
): WordChantThumbnailEntranceTiming {
  if (
    !Number.isInteger(wordCount) ||
    wordCount < WORD_CHANT_MIN_WORD_COUNT ||
    wordCount > WORD_CHANT_MAX_WORD_COUNT
  ) {
    throw new RangeError(
      `Word Chant word count must be between ${WORD_CHANT_MIN_WORD_COUNT} and ${WORD_CHANT_MAX_WORD_COUNT}`,
    );
  }
  if (!Number.isInteger(wordIndex) || wordIndex < 0 || wordIndex >= wordCount) {
    throw new RangeError('Word Chant word index is out of range');
  }
  const transitionEndFrame =
    recordedSceneTransitionDuration(sceneDurationFrames);
  const availableFrames = Math.max(0, sceneDurationFrames - transitionEndFrame);
  if (availableFrames <= wordCount) {
    return {
      startFrame: 0,
      durationInFrames: 1,
      settledFrame: 0,
      isImmediate: true,
    };
  }
  const holdFrames = Math.min(
    24,
    Math.floor(availableFrames / 3),
    availableFrames - wordCount,
  );
  const revealFrames = Math.max(0, availableFrames - holdFrames);
  const durationInFrames = Math.max(
    1,
    Math.min(14, revealFrames - (wordCount - 1)),
  );
  const staggerFrames = Math.min(
    8,
    Math.floor(Math.max(0, revealFrames - durationInFrames) / (wordCount - 1)),
  );
  const startFrame = transitionEndFrame + wordIndex * staggerFrames;
  return {
    startFrame,
    durationInFrames,
    settledFrame: startFrame + durationInFrames,
    isImmediate: false,
  };
}

export function recordedInterludeThumbnailScale(wordCount: number): number {
  if (
    !Number.isInteger(wordCount) ||
    wordCount < WORD_CHANT_MIN_WORD_COUNT ||
    wordCount > WORD_CHANT_MAX_WORD_COUNT
  ) {
    throw new RangeError(
      `Word Chant word count must be between ${WORD_CHANT_MIN_WORD_COUNT} and ${WORD_CHANT_MAX_WORD_COUNT}`,
    );
  }
  return Math.round((1.2 - (wordCount - 3) * 0.08) * 100) / 100;
}

export function recordedVisualSequenceDuration(
  fromFrame: number,
  endFrame: number,
): number {
  return Math.max(1, endFrame - fromFrame);
}

export function recordedSceneTransitionDuration(
  sceneDurationFrames: number,
): number {
  const recordedShowcaseMinimumFrames = 12;
  return Math.min(
    RECORDED_SCENE_TRANSITION_MAX_FRAMES,
    Math.max(0, sceneDurationFrames - recordedShowcaseMinimumFrames),
  );
}

export function recordedSceneTransitionProgress(
  frame: number,
  durationInFrames: number,
): number {
  if (durationInFrames <= 1) return 1;
  return progressForFrame(frame, durationInFrames);
}

export function isRecordedResponseCueVisible(
  localFrame: number,
  cueFrames: readonly [number, number],
): boolean {
  return cueFrames[0] <= localFrame && localFrame < cueFrames[1];
}

export function recordedCueDisplayText(text: string): string | null {
  return resolveWordChantDefaultCueDisplayText(text);
}

export function recordedCueRenderedText(
  sourceText: string,
  editedDisplayText?: string,
): string | null {
  const defaultDisplayText = recordedCueDisplayText(sourceText);
  if (defaultDisplayText === null) return null;
  return editedDisplayText?.trim() || defaultDisplayText;
}

export function cueReaction(
  localFrame: number,
  cueLocalFrame: number,
  reactionFrames = 6,
): number {
  return Math.max(0, 1 - Math.abs(localFrame - cueLocalFrame) / reactionFrames);
}
